import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageContainer from '../components/common/PageContainer';
import ProductCard from '../components/common/ProductCard';
import ProductRating from '../components/common/ProductRating';
import storeApi from '../services/storeApi';
import { imagePath } from '../utils/imagePath';

/**
 * ProductDetailsPage - Product details page
 * Follows React Rules: Side effects in useEffect, proper state management
 */
const ProductDetailsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState(null);

  // Fetch product details
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const productData = await storeApi.getProduct(slug);
        setProduct(productData);
        
        // Set primary image as selected
        if (productData.primary_image_url) {
          const primaryImg = productData.primary_image_url.startsWith('http') 
            ? productData.primary_image_url 
            : imagePath(productData.primary_image_url);
          setSelectedImage(primaryImg);
        } else {
          setSelectedImage(imagePath('product/large-size/1.jpg'));
        }

        // Fetch related products (same category)
        if (productData.category?.id) {
          try {
            const related = await storeApi.getProducts({
              category: productData.category.id,
              page_size: 4
            });
            const relatedList = related.results || related;
            // Exclude current product
            setRelatedProducts(relatedList.filter(p => p.id !== productData.id));
          } catch (err) {
            console.error('Error fetching related products:', err);
          }
        }

        // Fetch reviews
        if (productData.id) {
          try {
            const reviewsData = await storeApi.getProductReviews(productData.id);
            setReviews(Array.isArray(reviewsData) ? reviewsData : (reviewsData.results || []));
          } catch (err) {
            console.error('Error fetching reviews:', err);
          }
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Product not found');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchProduct();
    }
  }, [slug]);

  // Helper function to convert relative URLs to absolute URLs for venobox
  const getAbsoluteUrl = (url) => {
    if (!url) return imagePath('product/large-size/1.jpg');
    
    // If already absolute, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // If it's a media URL from Django, prepend the backend URL
    if (url.startsWith('/media/')) {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      return `${backendUrl}${url}`;
    }
    
    // For store assets, use the imagePath utility
    return imagePath(url);
  };

  // Process images - ensure all are valid URLs (computed early for use in useEffect)
  const primaryImage = product?.primary_image_url 
    ? getAbsoluteUrl(product.primary_image_url)
    : imagePath('product/large-size/1.jpg');
  
  const secondaryImage = product?.secondary_image_url
    ? getAbsoluteUrl(product.secondary_image_url)
    : null;
  
  const images = [primaryImage];
  if (secondaryImage) images.push(secondaryImage);
  
  // Add product images from the product_images array
  if (product?.product_images && product.product_images.length > 0) {
    product.product_images.forEach(img => {
      if (img.image_url) {
        const imgUrl = getAbsoluteUrl(img.image_url);
        if (!images.includes(imgUrl)) {
          images.push(imgUrl);
        }
      }
    });
  }

  // Debug: Log images for troubleshooting
  useEffect(() => {
    if (product) {
      console.log('Product images:', {
        primary_image_url: product.primary_image_url,
        secondary_image_url: product.secondary_image_url,
        product_images: product.product_images,
        processedImages: images
      });
    }
  }, [product, images]);

  // Initialize slick slider for product images and venobox
  // Note: main.js also initializes these, so we need to prevent conflicts
  useEffect(() => {
    if (!product || images.length === 0) return;
    
    // Prevent main.js from initializing these sliders by adding a data attribute
    const preventMainJsInit = () => {
      const detailsImages = document.querySelector('.product-details-images.slider-navigation-1');
      const detailsThumbs = document.querySelector('.product-details-thumbs.slider-thumbs-1');
      
      if (detailsImages) {
        detailsImages.setAttribute('data-slick-initialized', 'react');
      }
      if (detailsThumbs) {
        detailsThumbs.setAttribute('data-slick-initialized', 'react');
      }
    };
    
    // Wait for slick to be available and DOM to be ready
    const initSlickSliders = (retryCount = 0) => {
      if (retryCount > 30) {
        console.warn('Slick slider initialization failed after multiple retries');
        return;
      }

      if (!window.jQuery || typeof window.jQuery.fn.slick !== 'function') {
        setTimeout(() => initSlickSliders(retryCount + 1), 100);
        return;
      }

      const $detailsImages = window.jQuery('.product-details-images.slider-navigation-1');
      const $detailsThumbs = window.jQuery('.product-details-thumbs.slider-thumbs-1');
      
      if (!$detailsImages.length || !$detailsThumbs.length) {
        setTimeout(() => initSlickSliders(retryCount + 1), 100);
        return;
      }

      // Mark as initialized by React to prevent main.js from interfering
      preventMainJsInit();

      // Destroy existing sliders if any (including from main.js)
      try {
        if ($detailsImages.hasClass('slick-initialized')) {
          $detailsImages.slick('unslick');
        }
        if ($detailsThumbs.hasClass('slick-initialized')) {
          $detailsThumbs.slick('unslick');
        }
      } catch (e) {
        // Ignore cleanup errors
      }

      // Wait a bit more to ensure DOM is fully ready
      setTimeout(() => {
        // Initialize thumbnail slider FIRST (horizontal scroller with navigation)
        // This creates the horizontal scrolling thumbnails below the main image
        if (images.length > 1) {
          $detailsThumbs.slick({
            slidesToShow: 4,
            slidesToScroll: 1,
            autoplay: false,
            dots: false,
            infinite: true,
            focusOnSelect: true,
            centerMode: true,
            centerPadding: 0,
            prevArrow: '<span class="slick-prev"><i class="fa fa-angle-left"></i></span>',
            nextArrow: '<span class="slick-next"><i class="fa fa-angle-right"></i></span>',
            asNavFor: $detailsImages,
            responsive: [
              {
                breakpoint: 992,
                settings: {
                  slidesToShow: 3,
                }
              },
              {
                breakpoint: 768,
                settings: {
                  slidesToShow: 2,
                }
              }
            ]
          });
        }

        // Initialize main image slider (synced with thumbs)
        $detailsImages.slick({
          arrows: false,
          slidesToShow: 1,
          slidesToScroll: 1,
          autoplay: false,
          dots: false,
          infinite: true,
          centerMode: false,
          centerPadding: 0,
          asNavFor: $detailsThumbs,
          fade: false,
          adaptiveHeight: true
        });

        // Initialize venobox for image popups - must be after slick is initialized
        // Reinitialize venobox to bind to the transformed DOM elements
        setTimeout(() => {
          if (window.jQuery && typeof window.jQuery.fn.venobox === 'function') {
            // Destroy existing venobox bindings first to avoid conflicts
            window.jQuery('.venobox').off('click.venobox');
            
            // Ensure all venobox links have absolute URLs
            window.jQuery('.venobox').each(function() {
              const $link = window.jQuery(this);
              let href = $link.attr('href');
              
              // Convert relative /media/ URLs to absolute
              if (href && href.startsWith('/media/') && !href.startsWith('http')) {
                // Get base URL - use API URL if available, otherwise use current origin
                const apiBaseUrl = process.env.REACT_APP_API_URL || '';
                const baseUrl = apiBaseUrl.replace('/api', '').replace(/\/$/, '') || window.location.origin;
                $link.attr('href', `${baseUrl}${href}`);
              }
              
              // Log for debugging
              console.log('Venobox link href:', $link.attr('href'));
            });
            
            // Initialize venobox with proper configuration matching main.js
            window.jQuery('.venobox').venobox({
              spinner: 'wave',
              spinColor: '#cb9a00',
              numeratio: true,
              infinigall: true,
              // Add error handling and timeout
              onContentLoaded: function() {
                console.log('Venobox content loaded successfully');
              },
              onError: function() {
                console.error('Venobox error loading image');
                // Close venobox on error to prevent infinite loading
                if (window.jQuery && window.jQuery.fn.venobox) {
                  setTimeout(() => {
                    window.jQuery('.vbox-close').trigger('click');
                  }, 2000);
                }
              },
              // Add timeout to prevent infinite loading
              timeout: 10000
            });
          } else {
            console.warn('Venobox not available - check if venobox.min.js is loaded');
          }
        }, 400);

        // Initialize Bootstrap tabs
        if (window.jQuery && window.jQuery.fn.tab) {
          window.jQuery('[data-toggle="tab"]').on('click', function(e) {
            e.preventDefault();
            window.jQuery(this).tab('show');
          });
        }
      }, 150);
    };

    // Start initialization - wait longer to ensure main.js has run (or not run)
    const timer = setTimeout(() => initSlickSliders(), 1000);

    return () => {
      clearTimeout(timer);
      if (window.jQuery && typeof window.jQuery.fn.slick === 'function') {
        const $detailsImages = window.jQuery('.product-details-images.slider-navigation-1');
        const $detailsThumbs = window.jQuery('.product-details-thumbs.slider-thumbs-1');
        
        try {
          if ($detailsImages.hasClass('slick-initialized')) {
            $detailsImages.slick('unslick');
          }
          if ($detailsThumbs.hasClass('slick-initialized')) {
            $detailsThumbs.slick('unslick');
          }
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [product, images.length]);

  const handleAddToCart = async () => {
    if (!product) return;
    
    try {
      await storeApi.addToCart(product.id, quantity);
      // Trigger cart update event for header
      window.dispatchEvent(new Event('cartUpdated'));
      alert('Product added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (error.response?.status === 401) {
        alert('Please login to add items to cart');
      } else {
        alert('Failed to add product to cart. Please try again.');
      }
    }
  };

  const handleAddToWishlist = async () => {
    if (!product) return;
    
    try {
      await storeApi.addToWishlist(product.id);
      alert('Product added to wishlist!');
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      alert('Please login to add items to wishlist');
    }
  };

  const handleQuantityChange = (delta) => {
    setQuantity(prev => {
      const newQuantity = prev + delta;
      return newQuantity < 1 ? 1 : newQuantity;
    });
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="text-center py-5">
          <p>Loading product...</p>
        </div>
      </PageContainer>
    );
  }

  if (error || !product) {
    return (
      <PageContainer>
        <div className="text-center py-5">
          <p className="text-danger">{error || 'Product not found'}</p>
          <button onClick={() => navigate('/store/shop')} className="btn">
            Back to Shop
          </button>
        </div>
      </PageContainer>
    );
  }

  // Process images - ensure all are valid URLs

  return (
    <PageContainer>
      {/* content-wraper start */}
      <div className="content-wraper">
        <div className="container">
          <div className="row single-product-area">
            {/* Product Images */}
            <div className="col-lg-5 col-md-6">
              <div className="product-details-left">
                {images.length > 0 ? (
                  <>
                    <div className="product-details-images slider-navigation-1" data-react-slider="true">
                      {images.map((img, index) => {
                        // For venobox, ensure we use absolute URLs
                        // If image is from /media/, it should already be absolute from API
                        // But if it's a relative path, convert it
                        const venoboxUrl = img.startsWith('http') ? img : 
                          (img.startsWith('/media/') ? `${window.location.origin}${img}` : img);
                        
                        return (
                          <div key={index} className="lg-image">
                            <a 
                              className="popup-img venobox vbox-item" 
                              href={venoboxUrl} 
                              data-gall="myGallery"
                            >
                              <img
                                src={img}
                                alt={`${product.name} ${index + 1}`}
                                onError={(e) => {
                                  console.error('Image failed to load:', e.target.src);
                                  const fallback = imagePath('product/large-size/1.jpg');
                                  e.target.src = fallback;
                                  // Update the href for venobox as well
                                  const link = e.target.closest('a');
                                  if (link) {
                                    link.href = fallback;
                                  }
                                }}
                              />
                            </a>
                          </div>
                        );
                      })}
                    </div>
                    {images.length > 1 && (
                      <div className="product-details-thumbs slider-thumbs-1" data-react-slider="true" style={{ marginTop: '15px' }}>
                        {images.map((img, index) => (
                          <div key={index} className="sm-image" style={{ cursor: 'pointer', padding: '5px' }}>
                            <img 
                              src={img} 
                              alt={`${product.name} ${index + 1}`}
                              style={{ width: '100%', display: 'block' }}
                              onError={(e) => {
                                console.error('Thumbnail image failed to load:', e.target.src);
                                e.target.src = imagePath('product/small-size/1.jpg');
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="product-details-images">
                    <div className="lg-image">
                      <a className="popup-img venobox vbox-item" href={imagePath('product/large-size/1.jpg')} data-gall="myGallery">
                        <img
                          src={imagePath('product/large-size/1.jpg')}
                          alt={product.name}
                        />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Product Info */}
            <div className="col-lg-7 col-md-6">
              <div className="product-details-view-content pt-60">
                <div className="product-info">
                  <h2>{product.name}</h2>
                  {product.sku && (
                    <span className="product-details-ref">Reference: {product.sku}</span>
                  )}
                  
                  <div className="rating-box pt-20">
                    <ul className="rating rating-with-review-item">
                      {Array.from({ length: Math.min(5, Math.round(product.average_rating || 0)) }, (_, i) => (
                        <li key={`full-${i}`}>
                          <i className="fa fa-star-o"></i>
                        </li>
                      ))}
                      {Array.from({ length: Math.max(0, 5 - Math.round(product.average_rating || 0)) }, (_, i) => (
                        <li key={`empty-${i}`} className="no-star">
                          <i className="fa fa-star-o"></i>
                        </li>
                      ))}
                      <li className="review-item"><a href="#reviews">Read Review</a></li>
                      <li className="review-item"><a href="#reviews">Write Review</a></li>
                    </ul>
                  </div>

                  <div className="price-box pt-20">
                    {product.compare_at_price && parseFloat(product.compare_at_price) > parseFloat(product.price) ? (
                      <>
                        <span className="new-price new-price-2">PKR {parseFloat(product.price).toFixed(2)}</span>
                        <span className="old-price">PKR {parseFloat(product.compare_at_price).toFixed(2)}</span>
                      </>
                    ) : (
                      <span className="new-price new-price-2">PKR {parseFloat(product.price).toFixed(2)}</span>
                    )}
                  </div>

                  {product.short_description && (
                    <div className="product-desc">
                      <p>
                        <span>{product.short_description}</span>
                      </p>
                    </div>
                  )}

                  {/* Product Variants - Placeholder for future variants */}
                  <div className="product-variants">
                    {/* Can be extended for size, color, etc. */}
                  </div>

                  <div className="single-add-to-cart">
                    <form className="cart-quantity" onSubmit={(e) => { e.preventDefault(); handleAddToCart(); }}>
                      <div className="quantity">
                        <label>Quantity</label>
                        <div className="cart-plus-minus">
                          <input className="cart-plus-minus-box" value={quantity} type="text" readOnly />
                          <div className="dec qtybutton" onClick={() => handleQuantityChange(-1)}>
                            <i className="fa fa-angle-down"></i>
                          </div>
                          <div className="inc qtybutton" onClick={() => handleQuantityChange(1)}>
                            <i className="fa fa-angle-up"></i>
                          </div>
                        </div>
                      </div>
                      <button className="add-to-cart" type="submit" disabled={!product.is_in_stock}>
                        Add to cart
                      </button>
                    </form>
                  </div>

                  <div className="product-additional-info pt-25">
                    <a className="wishlist-btn" href="#" onClick={(e) => { e.preventDefault(); handleAddToWishlist(); }}>
                      <i className="fa fa-heart-o"></i>Add to wishlist
                    </a>
                    <div className="product-social-sharing pt-25">
                      <ul style={{ display: 'flex', gap: '10px', listStyle: 'none', padding: 0, margin: 0 }}>
                        <li className="facebook">
                          <a href="#" onClick={(e) => { e.preventDefault(); window.open(`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`, '_blank'); }}>
                            <i className="fa fa-facebook"></i>Facebook
                          </a>
                        </li>
                        <li className="twitter">
                          <a href="#" onClick={(e) => { e.preventDefault(); window.open(`https://twitter.com/intent/tweet?url=${window.location.href}`, '_blank'); }}>
                            <i className="fa fa-twitter"></i>Twitter
                          </a>
                        </li>
                        <li className="instagram">
                          <a href="#" onClick={(e) => { e.preventDefault(); }}>
                            <i className="fa fa-instagram"></i>Instagram
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="block-reassurance">
                    <ul>
                      <li>
                        <div className="reassurance-item">
                          <div className="reassurance-icon">
                            <i className="fa fa-check-square-o"></i>
                          </div>
                          <p>Security policy (edit with Customer reassurance module)</p>
                        </div>
                      </li>
                      <li>
                        <div className="reassurance-item">
                          <div className="reassurance-icon">
                            <i className="fa fa-truck"></i>
                          </div>
                          <p>Delivery policy (edit with Customer reassurance module)</p>
                        </div>
                      </li>
                      <li>
                        <div className="reassurance-item">
                          <div className="reassurance-icon">
                            <i className="fa fa-exchange"></i>
                          </div>
                          <p>Return policy (edit with Customer reassurance module)</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* content-wraper end */}
      
      {/* Begin Product Area */}
      <div className="product-area pt-35">
        <div className="container">
          {/* Product Tabs (Description, Reviews, etc.) */}
          <div className="row">
            <div className="col-lg-12">
              <div className="li-product-tab">
                <ul className="nav li-product-menu">
                  <li><a className="active" data-toggle="tab" href="#description"><span>Description</span></a></li>
                  <li><a data-toggle="tab" href="#product-details"><span>Product Details</span></a></li>
                  <li><a data-toggle="tab" href="#reviews"><span>Reviews</span></a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="tab-content">
            <div id="description" className="tab-pane active show" role="tabpanel">
              <div className="product-description">
                {product.description ? (
                  <span dangerouslySetInnerHTML={{ __html: product.description }} />
                ) : (
                  <span>No description available.</span>
                )}
              </div>
            </div>
            <div id="product-details" className="tab-pane" role="tabpanel">
              <div className="product-details-manufacturer">
                {product.manufacturer && (
                  <a href="#">
                    {product.manufacturer.logo && (
                      <img src={product.manufacturer.logo} alt="Product Manufacturer Image" />
                    )}
                  </a>
                )}
                {product.sku && <p><span>Reference</span> {product.sku}</p>}
                {product.category && (
                  <p><span>Category</span> {product.category.name}</p>
                )}
                {product.manufacturer && (
                  <p><span>Manufacturer</span> {product.manufacturer.name}</p>
                )}
              </div>
            </div>
            <div id="reviews" className="tab-pane" role="tabpanel">
              <div className="product-reviews">
                {reviews.length > 0 ? (
                  reviews.map(review => (
                    <div key={review.id} className="product-details-comment-block">
                      <div className="comment-review">
                        <span>Grade</span>
                        <ul className="rating">
                          {Array.from({ length: Math.min(5, Math.round(review.rating || 0)) }, (_, i) => (
                            <li key={`full-${i}`}>
                              <i className="fa fa-star-o"></i>
                            </li>
                          ))}
                          {Array.from({ length: Math.max(0, 5 - Math.round(review.rating || 0)) }, (_, i) => (
                            <li key={`empty-${i}`} className="no-star">
                              <i className="fa fa-star-o"></i>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="comment-author-infos pt-25">
                        <span>{review.user_name || 'Anonymous'}</span>
                        <em>{new Date(review.created_at).toLocaleDateString()}</em>
                      </div>
                      {review.title && (
                        <div className="comment-details">
                          <h4 className="mb-10">{review.title}</h4>
                        </div>
                      )}
                      {review.comment && (
                        <div className="comment-details">
                          <p>{review.comment}</p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="product-details-comment-block">
                    <p>No reviews yet. Be the first to review this product!</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="row mt-60">
              <div className="col-lg-12">
                <h3>Related Products</h3>
                <div className="row">
                  {relatedProducts.map((relatedProduct) => (
                    <div key={relatedProduct.id} className="col-lg-3 col-md-6 mt-40">
                      <ProductCard
                        product={relatedProduct}
                        onAddToCart={handleAddToCart}
                        onAddToWishlist={handleAddToWishlist}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Product Area End */}
    </PageContainer>
  );
};

export default ProductDetailsPage;
